/**
 * A company's own details, as one set of controls.
 *
 * Shared by the add form and the edit dialog. They are two screens over one
 * endpoint pair and every field duplicated between them is a field that
 * eventually disagrees — the same reason CommercialTermsFields exists.
 *
 * PF and ESI establishment codes are included here and were on neither form
 * before. CompanyUpdate has always accepted them and nothing else in the
 * product sets them, so they were columns collected by nobody.
 *
 * The bank account number is the one field that cannot be seeded. CompanyOut
 * returns `bankAccountNumberMasked` and never the value — payouts read the
 * column server-side — so on edit the box starts empty and says what that
 * means. Seeding it with the mask would save "••••3210" back as the account
 * number the first time somebody pressed Save without touching it.
 */
import type { Company } from '../types';

export interface CompanyFieldValues {
  name: string;
  legalName: string;
  gstin: string;
  pan: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  bankName: string;
  bankAccountNumber: string;
  bankIfsc: string;
  pfEstablishmentCode: string;
  esiEstablishmentCode: string;
}

export const EMPTY_COMPANY: CompanyFieldValues = {
  name: '',
  legalName: '',
  gstin: '',
  pan: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  bankName: '',
  bankAccountNumber: '',
  bankIfsc: '',
  pfEstablishmentCode: '',
  esiEstablishmentCode: '',
};

/** Seed an edit form from a company row. */
export function companyToFields(company: Company): CompanyFieldValues {
  return {
    ...EMPTY_COMPANY,
    name: company.name,
    legalName: company.legalName ?? '',
    gstin: company.gstin ?? '',
    pan: company.pan ?? '',
    address: company.address ?? '',
    city: company.city ?? '',
    state: company.state ?? '',
    pincode: company.pincode ?? '',
    bankName: company.bankName ?? '',
    // Deliberately not seeded — see the file docstring.
    bankAccountNumber: '',
    bankIfsc: company.bankIfsc ?? '',
    pfEstablishmentCode: company.pfEstablishmentCode ?? '',
    esiEstablishmentCode: company.esiEstablishmentCode ?? '',
  };
}

/**
 * The payload for create or update.
 *
 * Blank optional fields go as null so the validators skip them rather than
 * rejecting an empty string. `omitBlankAccount` is what keeps an untouched
 * bank box from clearing a stored account number on edit: null would wipe it,
 * and omitting the key leaves it alone, because PATCH uses exclude_unset.
 */
export function companyPayload(
  values: CompanyFieldValues,
  { omitBlankAccount = false }: { omitBlankAccount?: boolean } = {},
): Record<string, unknown> {
  const body: Record<string, unknown> = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, value.trim() || null]),
  );
  body.name = values.name.trim();
  if (omitBlankAccount && values.bankAccountNumber.trim() === '') {
    delete body.bankAccountNumber;
  }
  return body;
}

export default function CompanyFields({
  values,
  onChange,
  accountHint,
}: {
  values: CompanyFieldValues;
  onChange: (next: CompanyFieldValues) => void;
  /** What an empty bank-account box means on this form. */
  accountHint?: string;
}) {
  const set =
    (key: keyof CompanyFieldValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...values, [key]: e.target.value });

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="label" htmlFor="cf-name">Company name</label>
        <input
          spellCheck={false}
          id="cf-name"
          className="input"
          value={values.name}
          onChange={set('name')}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="cf-legal">Legal name</label>
        <input
          spellCheck={false}
          id="cf-legal"
          className="input"
          value={values.legalName}
          onChange={set('legalName')}
        />
        <p className="mt-0.5 text-[11px] text-gray-400">
          Printed on invoices as the cheque payee, where it is set.
        </p>
      </div>

      <div>
        <label className="label" htmlFor="cf-gstin">GSTIN</label>
        <input
          spellCheck={false}
          id="cf-gstin"
          className="input font-mono"
          placeholder="33ABCDE1234F1Z5"
          value={values.gstin}
          onChange={set('gstin')}
        />
      </div>
      <div>
        <label className="label" htmlFor="cf-pan">PAN</label>
        <input
          spellCheck={false}
          id="cf-pan"
          className="input font-mono"
          placeholder="ABCDE1234F"
          value={values.pan}
          onChange={set('pan')}
        />
      </div>

      <div className="sm:col-span-2">
        <label className="label" htmlFor="cf-address">Address</label>
        <input
          spellCheck={false}
          id="cf-address"
          className="input"
          value={values.address}
          onChange={set('address')}
        />
      </div>

      <div>
        <label className="label" htmlFor="cf-city">City</label>
        <input
          spellCheck={false}
          id="cf-city"
          className="input"
          value={values.city}
          onChange={set('city')}
        />
      </div>
      <div>
        <label className="label" htmlFor="cf-state">State</label>
        <input
          spellCheck={false}
          id="cf-state"
          className="input"
          value={values.state}
          onChange={set('state')}
        />
        {/* Two things read this, and neither is obvious from the label. */}
        <p className="mt-0.5 text-[11px] text-gray-400">
          Selects Professional Tax slabs, and decides IGST against CGST+SGST on
          new invoices.
        </p>
      </div>

      <div>
        <label className="label" htmlFor="cf-pincode">Pincode</label>
        <input
          spellCheck={false}
          id="cf-pincode"
          className="input"
          value={values.pincode}
          onChange={set('pincode')}
        />
      </div>
      <div>
        <label className="label" htmlFor="cf-bank">Bank name</label>
        <input
          spellCheck={false}
          id="cf-bank"
          className="input"
          value={values.bankName}
          onChange={set('bankName')}
        />
      </div>

      <div>
        <label className="label" htmlFor="cf-acct">Bank account</label>
        <input
          spellCheck={false}
          id="cf-acct"
          className="input font-mono"
          value={values.bankAccountNumber}
          onChange={set('bankAccountNumber')}
        />
        {accountHint && (
          <p className="mt-0.5 text-[11px] text-gray-400">{accountHint}</p>
        )}
      </div>
      <div>
        <label className="label" htmlFor="cf-ifsc">IFSC</label>
        <input
          spellCheck={false}
          id="cf-ifsc"
          className="input font-mono"
          placeholder="HDFC0001234"
          value={values.bankIfsc}
          onChange={set('bankIfsc')}
        />
      </div>

      <div>
        <label className="label" htmlFor="cf-pf">PF establishment code</label>
        <input
          spellCheck={false}
          id="cf-pf"
          className="input font-mono"
          value={values.pfEstablishmentCode}
          onChange={set('pfEstablishmentCode')}
        />
      </div>
      <div>
        <label className="label" htmlFor="cf-esi">ESI establishment code</label>
        <input
          spellCheck={false}
          id="cf-esi"
          className="input font-mono"
          value={values.esiEstablishmentCode}
          onChange={set('esiEstablishmentCode')}
        />
      </div>
    </div>
  );
}
